import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdminDisputeDetail } from '../../hooks/useAdminDisputes';
import { Spinner, Button, Modal, Textarea, Select } from '../../components/ui';

const DISPUTE_REASONS = {
  notAsDescribed: 'Item not as described',
  notReceived: 'Item not received / winner no-show',
  sellerUnresponsive: 'Seller is unresponsive',
  winnerUnresponsive: 'Winner is unresponsive',
  damaged: 'Item was damaged',
};

const RESOLUTION_OPTIONS = [
  { value: 'buyerFavour', label: 'Winner\'s favour (refund buyer)' },
  { value: 'sellerFavour', label: 'Seller\'s favour (release payout)' },
  { value: 'split', label: 'Split decision (manual handling)' },
];

const RESOLUTION_LABELS = {
  buyerFavour: 'Resolved in winner\'s favour',
  sellerFavour: 'Resolved in seller\'s favour',
  split: 'Split decision',
};

function formatDate(date) {
  if (!date) return 'N/A';
  return new Intl.DateTimeFormat('en-IE', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(date instanceof Date ? date : date.toDate?.() || new Date(date));
}

function EvidenceGallery({ evidence = [], label }) {
  const [viewingImage, setViewingImage] = useState(null);

  if (!evidence || evidence.length === 0) return null;

  return (
    <>
      <div className="mt-3">
        <p className="text-cream/40 text-xs uppercase tracking-wide mb-2">{label}</p>
        <div className="flex flex-wrap gap-2">
          {evidence.map((url, idx) => (
            <button
              key={url}
              onClick={() => setViewingImage(url)}
              className="relative group rounded-lg overflow-hidden border border-cream/10 hover:border-primary/50 transition-colors"
            >
              <img
                src={url}
                alt={`Evidence ${idx + 1}`}
                className="w-20 h-20 object-cover"
              />
            </button>
          ))}
        </div>
      </div>

      <Modal
        isOpen={!!viewingImage}
        onClose={() => setViewingImage(null)}
        title="Evidence"
        dark
        size="lg"
      >
        <div className="flex items-center justify-center">
          <img
            src={viewingImage}
            alt="Evidence"
            className="max-w-full max-h-[70vh] object-contain rounded-lg"
          />
        </div>
      </Modal>
    </>
  );
}

function UserCard({ user, role }) {
  if (!user) return null;

  return (
    <div className="bg-midnight rounded-lg p-4 border border-cream/10">
      <p className="text-cream/50 text-xs uppercase tracking-wide mb-2">{role}</p>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
          {user.displayName?.[0]?.toUpperCase() || '?'}
        </div>
        <div>
          <p className="text-cream font-medium">{user.displayName || 'Unknown'}</p>
          <p className="text-cream/50 text-sm">{user.email}</p>
        </div>
      </div>
    </div>
  );
}

export default function DisputeDetail() {
  const { id } = useParams();
  const {
    dispute,
    raffle,
    raiser,
    responder,
    loading,
    error,
    resolve,
    resolving,
  } = useAdminDisputeDetail(id);

  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [resolveError, setResolveError] = useState(null);

  async function handleResolve() {
    if (!resolution) {
      setResolveError('Please select a resolution');
      return;
    }

    try {
      await resolve(resolution, adminNotes);
      setShowResolveModal(false);
      setResolution('');
      setAdminNotes('');
    } catch (err) {
      setResolveError(err.message || 'Failed to resolve dispute');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !dispute) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-bold text-cream mb-4">
            {error || 'Dispute Not Found'}
          </h1>
          <Link to="/admin/disputes">
            <Button>Back to Disputes</Button>
          </Link>
        </div>
      </div>
    );
  }

  const reasonLabel = DISPUTE_REASONS[dispute.reason] || dispute.reason;
  const isResolved = dispute.status === 'resolved';

  return (
    <div className="min-h-screen bg-midnight">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Back link */}
        <Link
          to="/admin/disputes"
          className="inline-flex items-center gap-2 text-cream/60 hover:text-primary transition-colors mb-6"
        >
          <BackIcon className="w-4 h-4" />
          Back to disputes
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-display font-bold text-cream">
                Dispute Details
              </h1>
              <StatusBadge status={dispute.status} />
            </div>
            <p className="text-cream/60">
              Created {formatDate(dispute.createdAt)}
            </p>
          </div>

          {!isResolved && (
            <Button onClick={() => setShowResolveModal(true)} variant="primary">
              Resolve Dispute
            </Button>
          )}
        </div>

        {/* Resolution banner (if resolved) */}
        {isResolved && dispute.resolution && (
          <div className="bg-success/10 border border-success/30 rounded-xl p-5 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-8 rounded-full bg-success/20 text-success flex items-center justify-center font-bold">
                ✓
              </span>
              <h3 className="text-lg font-semibold text-success">
                {RESOLUTION_LABELS[dispute.resolution] || dispute.resolution}
              </h3>
            </div>
            <p className="text-cream/60 text-sm">
              Resolved on {formatDate(dispute.resolvedAt)}
            </p>
            {dispute.adminNotes && (
              <div className="mt-3 pt-3 border-t border-success/20">
                <p className="text-cream/50 text-xs uppercase tracking-wide mb-1">Admin Notes</p>
                <p className="text-cream/80 text-sm">{dispute.adminNotes}</p>
              </div>
            )}
          </div>
        )}

        {/* Competition info */}
        {raffle && (
          <div className="bg-midnight-light rounded-xl border border-cream/10 p-5 mb-6">
            <h2 className="text-lg font-display font-semibold text-cream mb-4">
              Competition
            </h2>
            <div className="flex items-center gap-4">
              {raffle.photos?.[0] && (
                <img
                  src={raffle.photos[0]}
                  alt={raffle.title}
                  className="w-20 h-20 object-cover rounded-lg"
                />
              )}
              <div>
                <Link
                  to={`/competition/${raffle.id}`}
                  className="text-cream font-medium hover:text-primary transition-colors"
                >
                  {raffle.title}
                </Link>
                <p className="text-cream/50 text-sm">
                  {raffle.ticketsSold || 0} / {raffle.ticketQuantity} entries sold
                </p>
                <p className="text-cream/50 text-sm">
                  Status: {raffle.status}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Parties involved */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <UserCard user={raiser} role={`Dispute Raised By (${dispute.raisedByRole})`} />
          <UserCard user={responder} role={`Dispute Against (${dispute.againstUserRole})`} />
        </div>

        {/* Dispute details */}
        <div className="bg-midnight-light rounded-xl border border-cream/10 p-5 mb-6">
          <h2 className="text-lg font-display font-semibold text-cream mb-4">
            Issue Reported
          </h2>

          <div className="mb-4">
            <p className="text-cream/50 text-xs uppercase tracking-wide mb-1">Reason</p>
            <p className="text-cream font-medium">{reasonLabel}</p>
          </div>

          <div className="bg-midnight rounded-lg p-4 border border-midnight-lighter">
            <p className="text-cream/50 text-xs uppercase tracking-wide mb-2">Description</p>
            <p className="text-cream/80 text-sm leading-relaxed whitespace-pre-wrap">
              {dispute.description}
            </p>
            <EvidenceGallery evidence={dispute.evidence} label="Evidence" />
          </div>
        </div>

        {/* Response (if any) */}
        {dispute.responseDescription && (
          <div className="bg-midnight-light rounded-xl border border-cream/10 p-5 mb-6">
            <h2 className="text-lg font-display font-semibold text-cream mb-4">
              Response from {responder?.displayName || 'Other Party'}
            </h2>
            <p className="text-cream/50 text-xs mb-3">
              Responded on {formatDate(dispute.respondedAt)}
            </p>

            <div className="bg-midnight rounded-lg p-4 border border-midnight-lighter">
              <p className="text-cream/80 text-sm leading-relaxed whitespace-pre-wrap">
                {dispute.responseDescription}
              </p>
              <EvidenceGallery evidence={dispute.responseEvidence} label="Evidence" />
            </div>
          </div>
        )}

        {/* No response warning */}
        {!dispute.responseDescription && dispute.status === 'open' && (
          <div className="bg-accent/10 border border-accent/30 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <span className="text-accent text-xl">!</span>
              <div>
                <p className="text-cream font-medium">Awaiting Response</p>
                <p className="text-cream/60 text-sm">
                  The other party has not yet responded to this dispute.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        title="Resolve Dispute"
        dark
      >
        <div className="space-y-4">
          <p className="text-cream/70 text-sm">
            Select a resolution for this dispute. This action will trigger the
            appropriate payment actions.
          </p>

          <Select
            label="Resolution"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            options={[
              { value: '', label: 'Select resolution...' },
              ...RESOLUTION_OPTIONS,
            ]}
          />

          <Textarea
            label="Admin Notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Add any notes about the resolution..."
            rows={3}
          />

          {resolveError && (
            <p className="text-error text-sm">{resolveError}</p>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => setShowResolveModal(false)}
              disabled={resolving}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              loading={resolving}
              disabled={!resolution}
              className="flex-1"
            >
              Confirm Resolution
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    open: { label: 'Open', color: 'bg-accent text-midnight' },
    underReview: { label: 'Under Review', color: 'bg-primary text-midnight' },
    resolved: { label: 'Resolved', color: 'bg-success text-midnight' },
  };

  const { label, color } = config[status] || { label: status, color: 'bg-cream/20 text-cream' };

  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${color}`}>
      {label}
    </span>
  );
}

function BackIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}
